'use client';

import { ReactNode, use, useMemo, useState } from 'react';
import { parseDataset } from './helpers/parseDataset';
import { WebAPIChartSlicerData } from '../../globals/webApiTypes/elements/WebAPICBS';
import { translateInline } from '@/globals/translate/translateInline';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { textBlock } from '@/uiPrimitives/typography/text';
import { useCbsGraph } from './useCbsGraph';
import { inlineSizeContainerCSS } from './cbsGraph.css';
import { position } from '@/uiPrimitives/layout/position';
import { box } from '@/uiPrimitives/layout/box';
import { GraphView } from './GraphView';
import { DataTableView } from './DataTableView';
import { CBSStatlineDataset } from './cbsStatline.types';

const CurrentViewButton = ({ children }: { children: ReactNode }) => (
	<span
		className={classes(
			box({
				paddingBlockEnd: '-1 | small',
				paddingInline: '1 | h3',
				borderWidth: '3px',
				borderBlockEndStyle: 'solid',
				borderColor: 'positiveBlue',
			}),
			textBlock({ fontWeight: 'bold' }),
		)}
	>
		{children}
	</span>
);

const SwitchActiveViewButton = ({
	children,
	onClick,
}: {
	children: ReactNode;
	onClick: () => void;
}) => (
	<button
		onClick={onClick}
		className={classes(
			box({ paddingBlockEnd: '-1 | small', paddingInline: '1 | h3' }),
			textBlock({ color: 'positiveMuted' }),
		)}
	>
		{children}
	</button>
);

const CbsGraphClient = ({
	chartSlicerData,
	graphDataPromise,
}: {
	chartSlicerData: WebAPIChartSlicerData;
	graphDataPromise: Promise<CBSStatlineDataset>;
}) => {
	const [activeView, setActiveView] = useState<'graph' | 'table'>('graph');
	const { canvasRef } = useCbsGraph();
	const graphLabel = translateInline({ en: 'Graph', nl: 'Grafiek' });
	const tableLabel = translateInline({ en: 'Table', nl: 'Tabel' });

	const dataset = use<CBSStatlineDataset>(graphDataPromise); // use allows data that was being fetched on the server to be used in a client component

	const dataEntryList = useMemo(
		() => parseDataset(dataset, chartSlicerData),
		[dataset, chartSlicerData],
	);

	return (
		<>
			<div
				className={classes(
					shelf({}),
					box({
						borderBlockEndStyle: 'solid',
						borderColor: 'positiveMuted',
						borderWidth: '1px',
					}),
				)}
			>
				{activeView === 'graph' ? (
					<CurrentViewButton>{graphLabel}</CurrentViewButton>
				) : (
					<SwitchActiveViewButton onClick={() => setActiveView('graph')}>
						{graphLabel}
					</SwitchActiveViewButton>
				)}
				{activeView === 'table' ? (
					<CurrentViewButton>{tableLabel}</CurrentViewButton>
				) : (
					<SwitchActiveViewButton onClick={() => setActiveView('table')}>
						{tableLabel}
					</SwitchActiveViewButton>
				)}
			</div>

			<div
				ref={canvasRef}
				className={classes(
					box({ paddingBlockStart: '-2 | smaller' }),
					position({ position: 'relative' }),
					inlineSizeContainerCSS,
				)}
			>
				{activeView === 'graph' && (
					<GraphView
						chartSlicerData={chartSlicerData}
						dataEntryList={dataEntryList}
					/>
				)}
				{activeView === 'table' && (
					<DataTableView
						chartSlicerData={chartSlicerData}
						dataEntryList={dataEntryList}
					/>
				)}
			</div>
		</>
	);
};

export default CbsGraphClient;
